import {
  useGetDashboard,
  useGetDevices,
  useGetDocuments,
  useGetOrgTasks,
  useGetUser,
  useGetUsersByOrg,
  useGlobalDeviceContext,
  usePatchDashboardCardPreferences,
} from "@hooks";
import {
  DashboardActivityDetails,
  DashboardCardKey,
  type DashboardCard,
  type PatchDashboardCardPreferencesBody,
} from "@models";
import { ListSubheader, MenuItem, Skeleton, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PageBreadcrumb, PageHeader } from "../components";
import { DashboardCardFilterControl } from "../components/Dashboard/DashboardCardFilterControl";
import { DashboardActivityCard } from "../components/Dashboard/DashboardActivityCard";
import { DashboardOpenTasksCard } from "../components/Dashboard/DashboardOpenTasksCard";
import { DashboardOutdatedDueSoonCard } from "../components/Dashboard/DashboardOutdatedDueSoonCard";
import { DashboardOverallProgressCard } from "../components/Dashboard/DashboardOverallProgressCard";
import { DashboardRoutingDocumentsCard } from "../components/Dashboard/DashboardRoutingDocumentsCard";
import { DashboardUnresolvedCommentsCard } from "../components/Dashboard/DashboardUnresolvedCommentsCard";
import { deriveDashboardData } from "../components/Dashboard/dashboardDerivedData";
import {
  dashboardNeedsDocumentSupport,
  dashboardNeedsTaskSupport,
  getDashboardApprovalDocumentItems,
  getDashboardTaskItems,
  getDashboardUnresolvedCommentItems,
} from "../components/Dashboard/dashboardCardData";
import { CompactSelect } from "../components/ui/CompactSelect";

const DASHBOARD_CARD_CONTAINER_CLASS_NAME =
  "grid min-h-0 flex-1 grid-cols-1 auto-rows-fr gap-3 px-3 pb-2 sm:grid-cols-2 md:grid-cols-3";
const DASHBOARD_CARD_ITEM_CLASS_NAME = "h-full min-h-0 min-w-0";
const DASHBOARD_CARD_SCROLL_REGION_CLASS_NAME =
  "flex min-h-0 flex-1 flex-col overflow-hidden";
const DASHBOARD_GRID_LAYOUT_LABEL = "xs:1 sm:2 md:3 rows:fill";
const DASHBOARD_SKELETON_CARD_COUNT = 6;

const EMPTY_ACTIVITY: DashboardActivityDetails = {
  completedTasksLastWeek: [],
  fullyApprovedDocumentsLastWeek: [],
  resolvedCommentDocumentsLastWeek: [],
};

const getDeviceCreatedAtTime = (
  createdAt: Date | string | null | undefined
) => {
  if (!createdAt) {
    return 0;
  }

  const time = new Date(createdAt).getTime();
  return Number.isFinite(time) ? time : 0;
};

export const OrganizationDashboard = () => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeviceParam = searchParams.get("deviceId");
  const { data: devices = [], isLoading: isLoadingDevices } = useGetDevices({
    orgId,
  });
  const { activeGlobalDeviceId, setActiveGlobalDeviceId } =
    useGlobalDeviceContext();
  const latestDeviceId = useMemo(() => {
    const latestDevice = devices
      .slice()
      .sort(
        (firstDevice, secondDevice) =>
          getDeviceCreatedAtTime(secondDevice.createdAt) -
          getDeviceCreatedAtTime(firstDevice.createdAt)
      )[0];

    return latestDevice?.id ?? "";
  }, [devices]);
  const isAllDevicesParam = selectedDeviceParam === "all";
  const validDeviceIds = useMemo(
    () => new Set(devices.map((device) => device.id)),
    [devices]
  );
  const validSelectedDeviceParam =
    selectedDeviceParam && validDeviceIds.has(selectedDeviceParam)
      ? selectedDeviceParam
      : null;
  const validActiveGlobalDeviceId =
    activeGlobalDeviceId && validDeviceIds.has(activeGlobalDeviceId)
      ? activeGlobalDeviceId
      : null;
  const selectedDeviceId = isAllDevicesParam
    ? ""
    : (validSelectedDeviceParam ?? validActiveGlobalDeviceId ?? latestDeviceId);
  const { data: users = [] } = useGetUsersByOrg({ orgId, enabled: !!orgId });
  const { data: user } = useGetUser();
  const patchPreferences = usePatchDashboardCardPreferences();
  const {
    data: dashboard,
    isLoading,
    error,
  } = useGetDashboard({
    orgId,
    deviceId: selectedDeviceId || undefined,
    enabled: !!orgId && !isLoadingDevices,
  });
  const shouldLoadDashboardTasks = dashboardNeedsTaskSupport(dashboard);
  const shouldLoadDashboardDocuments = dashboardNeedsDocumentSupport(dashboard);
  const {
    data: dashboardTasks = [],
    isLoading: isLoadingDashboardTasks,
    error: dashboardTasksError,
  } = useGetOrgTasks({
    orgId,
    scope: "all",
    enabled: !!orgId && shouldLoadDashboardTasks,
  });
  const {
    data: dashboardDocuments = [],
    isLoading: isLoadingDashboardDocuments,
    error: dashboardDocumentsError,
  } = useGetDocuments(orgId, selectedDeviceId || "", {
    includeOrgDocs: true,
    enabled: !!orgId && shouldLoadDashboardDocuments,
    ...(selectedDeviceId
      ? {}
      : {
          queryKeyOrgId: orgId,
        }),
  });
  const derivedDashboard = useMemo(
    () =>
      dashboard
        ? deriveDashboardData({
            dashboard,
            tasks: shouldLoadDashboardTasks ? dashboardTasks : undefined,
            documents: shouldLoadDashboardDocuments
              ? dashboardDocuments
              : undefined,
            selectedDeviceId: selectedDeviceId || undefined,
          })
        : undefined,
    [
      dashboard,
      dashboardDocuments,
      dashboardTasks,
      selectedDeviceId,
      shouldLoadDashboardDocuments,
      shouldLoadDashboardTasks,
    ]
  );
  const isDashboardLoading =
    isLoading ||
    (shouldLoadDashboardTasks && isLoadingDashboardTasks) ||
    (shouldLoadDashboardDocuments && isLoadingDashboardDocuments);
  const dashboardError =
    error ||
    (shouldLoadDashboardTasks ? dashboardTasksError : null) ||
    (shouldLoadDashboardDocuments ? dashboardDocumentsError : null);

  const selectedDevice =
    devices.find((device) => device.id === selectedDeviceId) ?? null;
  const selectedDeviceValue = isAllDevicesParam ? "all" : selectedDeviceId;
  const deviceLabel = selectedDevice?.name || "All Devices";
  const dashboardCardsToRender = derivedDashboard?.cards ?? [];
  const overallProgressCard = dashboardCardsToRender.find(
    (card) => card.key === "overall-progress"
  );
  const gridDashboardCards = dashboardCardsToRender.filter(
    (card) => card.key !== "overall-progress"
  );
  const taskListFallbackDeviceId = useMemo(() => {
    if (selectedDeviceId) {
      return selectedDeviceId;
    }

    if (validActiveGlobalDeviceId) {
      return validActiveGlobalDeviceId;
    }

    return latestDeviceId;
  }, [latestDeviceId, selectedDeviceId, validActiveGlobalDeviceId]);

  useEffect(() => {
    if (!selectedDeviceId || selectedDeviceId === activeGlobalDeviceId) {
      return;
    }

    setActiveGlobalDeviceId(selectedDeviceId);
  }, [activeGlobalDeviceId, selectedDeviceId, setActiveGlobalDeviceId]);

  const handleDeviceSelect = (nextDeviceId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("deviceId", nextDeviceId);
    if (nextDeviceId !== "all") {
      setActiveGlobalDeviceId(nextDeviceId);
    }
    setSearchParams(nextParams);
  };

  const handleFiltersChange = (
    cardKey: DashboardCardKey,
    filters: PatchDashboardCardPreferencesBody["filters"]
  ) => {
    patchPreferences.mutate({
      orgId,
      cardKey,
      deviceId: selectedDeviceId || undefined,
      filters,
    });
  };

  const renderCardFilterActions = (card: DashboardCard) =>
    card.filterKeys.length > 0 ? (
      <DashboardCardFilterControl
        cardKey={card.key}
        title={card.title}
        filterKeys={card.filterKeys}
        filters={card.filters}
        users={users}
        onFiltersChange={handleFiltersChange}
      />
    ) : undefined;

  const renderDashboardCard = (card: DashboardCard) => {
    switch (card.key) {
      case "documents-currently-routing":
        return (
          <DashboardRoutingDocumentsCard
            key={card.key}
            documents={getDashboardApprovalDocumentItems(card)}
            count={card.count}
            orgId={orgId}
            selectedDeviceId={selectedDeviceId || undefined}
            fallbackDeviceId={taskListFallbackDeviceId || undefined}
            actions={renderCardFilterActions(card)}
            className={DASHBOARD_CARD_ITEM_CLASS_NAME}
          />
        );
      case "outdated-due-soon":
        return (
          <DashboardOutdatedDueSoonCard
            key={card.key}
            tasks={getDashboardTaskItems(card)}
            count={card.count}
            actions={renderCardFilterActions(card)}
            selectedDeviceId={selectedDeviceId || undefined}
            fallbackDeviceId={taskListFallbackDeviceId || undefined}
            className={DASHBOARD_CARD_ITEM_CLASS_NAME}
          />
        );
      case "open-tasks":
        return (
          <DashboardOpenTasksCard
            key={card.key}
            tasks={getDashboardTaskItems(card)}
            count={card.count}
            actions={renderCardFilterActions(card)}
            selectedDeviceId={selectedDeviceId || undefined}
            fallbackDeviceId={taskListFallbackDeviceId || undefined}
            className={DASHBOARD_CARD_ITEM_CLASS_NAME}
          />
        );
      case "activity-last-7-days":
        return (
          <DashboardActivityCard
            key={card.key}
            activity={card.activity ?? EMPTY_ACTIVITY}
            count={card.count}
            orgId={orgId}
            selectedDeviceId={selectedDeviceId || undefined}
            fallbackDeviceId={taskListFallbackDeviceId || undefined}
            className={DASHBOARD_CARD_ITEM_CLASS_NAME}
          />
        );
      case "unresolved-comments":
        return (
          <DashboardUnresolvedCommentsCard
            key={card.key}
            documents={getDashboardUnresolvedCommentItems(card)}
            count={card.count}
            actions={renderCardFilterActions(card)}
            selectedDeviceId={selectedDeviceId || undefined}
            className={DASHBOARD_CARD_ITEM_CLASS_NAME}
          />
        );
      case "overall-progress":
        return null;
      default: {
        const exhaustiveCardKey: never = card.key;
        return exhaustiveCardKey;
      }
    }
  };

  return (
    <div
      className="flex h-full min-h-0 flex-1 flex-col gap-y-3 overflow-hidden p-0"
      data-testid="dashboard-page"
    >
      <div className="shrink-0">
        <PageHeader
          pageTitleComponent={
            <PageBreadcrumb
              items={[
                {
                  label:
                    devices.length > 0 ? (
                      <CompactSelect
                        textSize="medium"
                        value={selectedDeviceValue}
                        onChange={handleDeviceSelect}
                        ariaLabel="Dashboard device"
                        renderValue={() => deviceLabel}
                      >
                        <ListSubheader>Show metrics for</ListSubheader>
                        <MenuItem value="all">All Devices</MenuItem>
                        {devices.map((device) => (
                          <MenuItem key={device.id} value={device.id}>
                            {device.name}
                          </MenuItem>
                        ))}
                      </CompactSelect>
                    ) : (
                      deviceLabel
                    ),
                },
                { label: "Dashboard" },
              ]}
            />
          }
          rightPanelToggleRequiresDevice={false}
        />
      </div>

      {isDashboardLoading && (
        <div
          className={DASHBOARD_CARD_SCROLL_REGION_CLASS_NAME}
          data-testid="dashboard-card-scroll-region"
        >
          <DashboardPageSkeleton />
        </div>
      )}

      {dashboardError && (
        <div className="flex flex-1 flex-col items-center justify-center">
          <Typography color="error">
            Failed to load dashboard metrics. Please try again.
          </Typography>
        </div>
      )}

      {!isDashboardLoading &&
        !dashboardError &&
        derivedDashboard?.cards.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <Typography variant="body1" color="text.secondary">
              No dashboard data available.
            </Typography>
          </div>
        )}

      {!isDashboardLoading &&
        !dashboardError &&
        derivedDashboard &&
        derivedDashboard.cards.length > 0 && (
          <div
            className={DASHBOARD_CARD_SCROLL_REGION_CLASS_NAME}
            data-testid="dashboard-card-scroll-region"
          >
            {overallProgressCard && (
              <DashboardOverallProgressCard
                title={overallProgressCard.title}
                progress={overallProgressCard.progress}
                userWorkload={overallProgressCard.userWorkload}
                firstName={user?.firstName}
                className="shrink-0 px-6 pb-4 pt-2"
              />
            )}
            {gridDashboardCards.length > 0 && (
              <div
                className={DASHBOARD_CARD_CONTAINER_CLASS_NAME}
                data-dashboard-grid-layout={DASHBOARD_GRID_LAYOUT_LABEL}
                data-testid="dashboard-card-container"
              >
                {gridDashboardCards.map(renderDashboardCard)}
              </div>
            )}
          </div>
        )}
    </div>
  );
};

const DashboardPageSkeleton = () => {
  const skeletonIds = Array.from(
    { length: DASHBOARD_SKELETON_CARD_COUNT },
    (_, index) => `dashboard-card-${index + 1}`
  );

  return (
    <>
      <DashboardOverallProgressSkeleton />
      <div
        className={DASHBOARD_CARD_CONTAINER_CLASS_NAME}
        data-dashboard-grid-layout={DASHBOARD_GRID_LAYOUT_LABEL}
        data-testid="dashboard-card-container"
      >
        {skeletonIds.map((id) => (
          <DashboardConnectedCardSkeleton key={id} />
        ))}
      </div>
    </>
  );
};

const DashboardOverallProgressSkeleton = () => (
  <section
    aria-hidden="true"
    className="min-w-0 shrink-0 px-3 pb-4 pt-2"
    data-testid="dashboard-overall-progress-skeleton"
  >
    <Skeleton
      variant="rounded"
      width="100%"
      height={260}
      data-testid="dashboard-overall-progress-skeleton-container"
    />
  </section>
);

const DashboardConnectedCardSkeleton = () => (
  <div
    aria-hidden="true"
    className={[
      "flex flex-col gap-0 rounded",
      DASHBOARD_CARD_ITEM_CLASS_NAME,
    ].join(" ")}
  >
    <Skeleton
      variant="rounded"
      width="100%"
      className="min-w-0 rounded-t"
      data-testid="dashboard-card-skeleton-header"
      style={{
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        height: "42px",
      }}
    />
    <Skeleton
      variant="rounded"
      width="100%"
      className="min-h-0 min-w-0 flex-1 rounded-b"
      data-testid="dashboard-card-skeleton-body"
      style={{
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
    />
  </div>
);
